import {
  CUSTOM_CHIP_ID,
  useJobPostingStore
} from "zustandStores/jobPostingStore";
import { useTemporaryJobPostingStore } from "zustandStores/temporaryJobPostingStore";
import useUserProfileStore from "zustandStores/userProfileStore";
import JobPostingService from "../../../services/JobPostingService";
import LogService from "../../../services/LogService";
import { JOB_POST_SUCCESS_NEW_USER } from "../../../services/webengage/webEngageConstants";
import {
  deleteLocalStorage,
  pageAfterJobPosting
} from "../../../services/utils";
import { reportException } from "logging";
import { FIELD_ERROR_MISSING_ERROR_CODE } from "services/loggingConstants";

const getFld = useJobPostingStore.getState().getFld;

// Helper function to get URL parameters
const getUrlParams = () => {
  if (typeof window === "undefined") return {};

  const urlParams = new URLSearchParams(window.location.search);
  const jobId = urlParams.get("job_id");
  const rePost = urlParams.get("re_post");
  const isDraft = urlParams.get("is_draft");

  return {
    jobId,
    rePost: rePost === "true",
    isDraft: isDraft === "true"
  };
};

// Helper function to check if current page is a job repost
const isJobRepostFromUrl = () => {
  const { jobId, rePost } = getUrlParams();
  return !!(rePost && jobId);
};

// Helper function to check if current page is in job edit mode
// Job edit: job_id exists but neither re_post nor is_draft exist (fields should be disabled)
export const isJobEditFromUrl = () => {
  const { jobId, rePost, isDraft } = getUrlParams();
  return !!(jobId && !rePost && !isDraft);
};

// Helper function to check if current page is draft with job ID
// Draft with job ID: both is_draft and job_id exist (fields should be enabled, go to confirmation)
export const isDraftWithJobIdFromUrl = () => {
  const { jobId, isDraft } = getUrlParams();
  return !!(isDraft && jobId);
};

export const getErrorMessage = (fieldId: string) => {
  const field = getFld(fieldId);
  if (fieldId == "salary" && field.error.errorMsg) {
    if (
      field.error.errorMsg === "Please enter salary range" &&
      !field.error.customError.isIncentivesError
    ) {
      return field.error.showError ? field.error.errorMsg : undefined;
    } else if (
      field.error.customError.isIncentivesError ||
      field.error.customError.isSalaryError
    ) {
      return field.error.errorMsg;
    }
  }
  if (
    field?.error?.isError &&
    field?.error?.showError &&
    field?.error?.errorMsg
  ) {
    return field.error.errorMsg;
  }
  return undefined;
};

// Reusable job posting function
export const submitJobPostV2 = async (
  jobPlan: "BASIC" | "PREMIUM" = undefined,
  options: {
    userProfile?: any;
    allowDuplicate?: boolean;
    isAutoJobPosted?: boolean;
    successCallback?: (response: any) => void;
    errorCallback?: (error: any) => void;
    postJobNow?: boolean;
  } = {}
) => {
  const {
    userProfile,
    allowDuplicate = false,
    isAutoJobPosted = false,
    successCallback,
    errorCallback,
    postJobNow = false
  } = options;

  try {
    // Get job data from draft payload
    const jobData = getDraftPayload();
    const { jobPlan: jobPlanFromStore } = useJobPostingStore.getState();
    // Add jobPlan to the job data
    const jobPostPayload = {
      ...jobData,
      jobPlan: jobPlan || jobPlanFromStore
    };

    // Use the new V2 service method
    const response = await JobPostingService.postJobV2(jobPostPayload, {
      // allowDuplicate,
      // isAutoJobPosted,
      successCallback: result => {
        console.log("Job posted successfully:", result);

        const { is_soft_profile_completed: softProfileCompleted } =
          userProfile || {};

        // Log new user event if needed
        if (!softProfileCompleted) {
          LogService.log(JOB_POST_SUCCESS_NEW_USER);
        }

        // Clear local storage
        // deleteLocalStorage();

        // Navigate to appropriate page
        if (userProfile) {
          pageAfterJobPosting(userProfile, {
            ...result,
            isDraft: result.is_draft
          });
        }

        // Execute external success callback
        if (successCallback) {
          successCallback(result);
        }
      },
      errorCallback: error => {
        console.error("Job posting failed:", error);

        // Execute external error callback
        if (errorCallback) {
          errorCallback(error);
        }
      },
      postJobNow
    });

    return response;
  } catch (error) {
    console.error("Job posting error:", error);

    // Execute external error callback
    if (errorCallback) {
      errorCallback(error);
    }

    throw error;
  }
};

export const extractFieldValues = (storeKey: string, value: any) => {
  const getExperienceRangeLabel = (minExp: number, maxExp: number): string => {
    const isInvalid = val => val === -1 || val === undefined;
    if (minExp > 72) {
      return "6+ years of experience";
    } else if (maxExp > 72) {
      if (isInvalid(minExp)) {
        return null;
      }
      if (minExp == 6) {
        return `6 months - 6+ years of experience`;
      } else if (minExp == 0) {
        return `0 - 6+ years of experience`;
      }
      return `${minExp / 12} - 6+ years of experience`;
    } else {
      if (isInvalid(minExp) || isInvalid(maxExp)) {
        return null;
      }
      if (maxExp == 6) {
        return `0 - 6 months of experience`;
      }
      if (minExp == 6) {
        return `${minExp} months - ${maxExp / 12} years of experience`;
      }
      return `${minExp / 12} - ${maxExp / 12} years of experience`;
    }
  };

  const getSalaryRange = (
    minSalary: number,
    maxSalary: number,
    incentives: number
  ) => {
    let maxWithIncentives = Number(maxSalary);
    if (incentives) {
      maxWithIncentives += Number(incentives);
    }
    return `₹${minSalary.toLocaleString()} - ₹${maxWithIncentives.toLocaleString()}`;
  };

  const getInterviewAddress = (interviewAddress: string) => {
    const interviewCityField = getFld("interview_city_id");
    const interviewLocalityField = getFld("interview_locality_id");
    let newAddress = interviewAddress;
    if (interviewLocalityField?.value?.label) {
      newAddress = newAddress + ", " + interviewLocalityField?.value?.label;
    }
    if (interviewCityField?.value?.label) {
      newAddress = newAddress + ", " + interviewCityField?.value?.label;
    }
    return newAddress || "";
  };

  if (!value || value === null || value === undefined || value === "") {
    return null;
  }

  switch (storeKey) {
    case "experience":
      if (typeof value === "object") {
        if (value.isFresher) return "fresher";
        return getExperienceRangeLabel(value.min, value.max);
      }
      return String(value);

    case "salary":
      if (value.min_salary && value.max_salary) {
        return getSalaryRange(
          value.min_salary,
          value.max_salary,
          value.incentives
        );
      }
      return null;

    case "communication_preference":
      if (typeof value === "object" && value.currentPhone) {
        return value.currentPhone;
      }
      return null;

    case "famous_location":
      if (
        typeof value === "object" &&
        value.famous_category_label &&
        value.famous_location_label
      ) {
        const singularCategory = value.famous_category_label.replace(/s$/, "");
        return `${singularCategory} - ${value.famous_location_label}`;
      }
      return null;

    case "skills":
      return value?.map(item => item.label || item.name || String(item)) || [];

    case "skills_preview":
      return (
        value
          ?.filter(item => item.id !== CUSTOM_CHIP_ID)
          ?.map(item => item.label) || []
      );
    case "jobBenefits_preview":
      return (
        value
          ?.filter(item => item.id !== CUSTOM_CHIP_ID)
          ?.map(item => item.label)
          .join(", ") || []
      );

    case "job_type":
      return value?.label ? value.label : null;

    case "work_location":
      return value?.label ? value.label : null;
    case "incentives":
      return value ? `₹${value.toLocaleString()}` : null;

    case "jobTimings":
      if (!value || !value.startTime || !value.endTime) {
        return null;
      }
      return `${value.startTime} - ${value.endTime}`;

    case "interview_address":
      return getInterviewAddress(value) || null;

    default:
      if (value === null || value === undefined || value === "") {
        return null;
      }

      if (Array.isArray(value)) {
        if (value.length === 0) return null;

        if (typeof value[0] === "object" && value[0] !== null) {
          return (
            value
              ?.filter(item => item?.label)
              .map(item => item?.label)
              .join(", ") || []
          );
        }
        return null; // For other array types, show dash
      }

      if (typeof value === "object") {
        return value.label ? value.label : null;
      }

      if (typeof value === "boolean") {
        return value ? "Yes" : "No";
      }

      return String(value);
  }
};

export const getJobDataMap = () => {
  const profileData = useUserProfileStore.getState().profileData;
  const { is_consultant = false, company = {} } = profileData || {};
  const { company_name = "" } = company || {};
  const jobTitleField = getFld("job_title");
  const categoryField = getFld("category_id");
  const cityField = getFld("job_city_id");
  const localityField = getFld("job_locality_id");
  const experienceField = getFld("experience");
  const qualificationField = getFld("qualification");
  const salaryField = getFld("salary");
  const workingDaysField = getFld("workingDays");
  const shiftTypeField = getFld("shiftType");
  const skillsField = getFld("skills");
  const industryField = getFld("industry");
  const clientNameField = getFld("client_name");
  const vacancyField = getFld("vacancy");
  const regionalLanguagesField = getFld("regionalLanguages");
  const incentivesField = getFld("incentives");
  const genderField = getFld("gender");
  const jobTimingsField = getFld("jobTimings");
  const jobBenefitsField = getFld("jobBenefits");
  const interviewTypeField = getFld("interviewType");
  const interviewAddressField = getFld("interview_address");

  const placeholderValueMap = {
    jobTitle: extractFieldValues(jobTitleField?.id, jobTitleField?.value),
    jobCategory: extractFieldValues(categoryField?.id, categoryField?.value),
    companyName: is_consultant
      ? `${extractFieldValues(clientNameField?.id, clientNameField?.value)}`
      : `${company_name}`,
    jobCity: extractFieldValues(cityField?.id, cityField?.value),
    JobLocality: extractFieldValues(localityField?.id, localityField?.value),
    industryLabel: extractFieldValues(industryField?.id, industryField?.value),
    experienceRange: extractFieldValues(
      experienceField?.id,
      experienceField?.value
    ),
    regionalLanguages: extractFieldValues(
      regionalLanguagesField?.id,
      regionalLanguagesField?.value
    ),
    minQualification: extractFieldValues(
      qualificationField?.id,
      qualificationField?.value
    ),
    workingDays: extractFieldValues(
      workingDaysField?.id,
      workingDaysField?.value
    ),
    shiftType: extractFieldValues(shiftTypeField?.id, shiftTypeField?.value),
    inHandSalaryRange: extractFieldValues(salaryField?.id, salaryField?.value),
    numOpening: extractFieldValues(vacancyField?.id, vacancyField?.value),
    jobSkills: extractFieldValues(skillsField?.id, skillsField?.value),
    jobSkillsPreview: extractFieldValues(
      `${skillsField?.id}_preview`,
      skillsField?.value
    ),
    industry: extractFieldValues(industryField?.id, industryField?.value),
    incentives: extractFieldValues(
      salaryField?.id,
      salaryField?.value?.incentives
    ),
    gender: extractFieldValues(genderField?.id, genderField?.value),
    jobTimings: extractFieldValues(jobTimingsField?.id, jobTimingsField?.value),
    jobBenefits: extractFieldValues(
      jobBenefitsField?.id,
      jobBenefitsField?.value
    ),
    jobBenefitsPreview: extractFieldValues(
      `${jobBenefitsField?.id}_preview`,
      jobBenefitsField?.value
    ),
    interviewType: extractFieldValues(
      interviewTypeField?.id,
      interviewTypeField?.value
    ),
    interviewAddress: extractFieldValues(
      interviewAddressField?.id,
      interviewAddressField?.value
    )
  };

  return placeholderValueMap;
};

const handleOptionTypeFields = (data: any) => {
  const formattedData = [];
  // data can be an array or just a single object
  if (Array.isArray(data)) {
    data.forEach(item => {
      if (item.id === null || item.id === undefined || item.id === "") {
        return null;
      }
      if (item.id === CUSTOM_CHIP_ID) {
        formattedData.push({ option_id: item.id, label: item.label });
      } else {
        formattedData.push({ option_id: item.id });
      }
    });
  } else {
    if (data.id === CUSTOM_CHIP_ID) {
      if (data.id === null || data.id === undefined || data.id === "") {
        return null;
      }
      return { option_id: data.id, label: data.label };
    } else {
      if (data.id === null || data.id === undefined || data.id === "") {
        return null;
      }
      return { option_id: data.id };
    }
  }
  return formattedData;
};
const formatFieldValue = (field: any) => {
  if (!field || field.value === undefined || field.value === null) {
    return null;
  }

  // Handle object values with id/label structure
  if (typeof field.value === "object" && field.value !== null) {
    // If it's an array, process each item
    if (Array.isArray(field.value)) {
      const processedArray = field.value
        .map(item => {
          if (typeof item === "object" && item !== null && "id" in item) {
            return item.id;
          }
          return item;
        })
        .filter(item => item !== null && item !== undefined);

      // Return null for empty arrays
      return processedArray.length > 0 ? processedArray : null;
    }

    // If it's an object with id property, return just the id
    if (
      "id" in field.value &&
      field.value.id !== null &&
      field.value.id !== undefined
    ) {
      return field.value.id;
    }

    // For other objects, return as is (but exclude options, error, etc.)
    const { options, error, ...cleanValue } = field.value;
    return cleanValue;
  }

  return field.value;
};

const isValueNull = (value: any) => {
  if (value === null || value === undefined || value === "") {
    return true;
  } else if (Array.isArray(value)) {
    if (value && value.length === 0) {
      return true;
    }
  } else if (typeof value === "object") {
    if (value.id === null || value.id === undefined || value.id === "") {
      return true;
    }
  }
  return false;
};

const getAllAvailableFields = (localState: any[]) => {
  const { profileData } = useUserProfileStore.getState();
  const { is_consultant = false } = profileData || {};
  return localState
    .filter(field => {
      // Include field if it's visible and has a meaningful value
      if (!field.visible) return false;
      if (field.value === undefined || field.value === null) return false;
      if (field.error?.isError) return false;
      if (!field.error) {
        try {
          reportException({
            error: { message: "Field error is missing" },
            errorCode: FIELD_ERROR_MISSING_ERROR_CODE,
            errorInfo: JSON.stringify(field ?? {})
          });
        } catch (error) {
          console.log("error", error);
        }
      }

      // Handle empty strings (but keep them if they might be meaningful)
      if (
        field.value === "" &&
        field.id !== "client_name" &&
        field.id !== "interview_address"
      )
        return false;

      // Handle empty arrays
      if (Array.isArray(field.value) && field.value.length === 0) return false;

      // Handle objects with null id
      if (
        typeof field.value === "object" &&
        field.value !== null &&
        "id" in field.value &&
        field.value.id === null
      )
        return false;

      return true;
    })
    .reduce((acc, field) => {
      // Handle each field individually with custom key names and formatting
      let fieldsToAdd: Record<string, any> = {};

      switch (field.id) {
        case "vacancy":
          if (!isValueNull(field.value)) {
            fieldsToAdd = {
              no_of_openings: field.value
            };
          }
          break;

        case "job_title":
          if (!isValueNull(field.value)) {
            fieldsToAdd = {
              job_title: field.value
            };
          }
          break;

        case "client_name":
          if (!isValueNull(field.value)) {
            fieldsToAdd = {
              client_name: field.value
            };
          }
          break;

        case "job_city_id":
          if (!isValueNull(field.value)) {
            fieldsToAdd = {
              job_city_id: field.value.id
            };
          }
          break;

        case "interview_city_id":
          if (!isValueNull(field.value)) {
            fieldsToAdd = {
              interview_city_id: field.value.id
            };
          }
          break;

        case "job_locality_id":
          if (!isValueNull(field.value)) {
            fieldsToAdd = {
              job_locality_id: field.value.id
            };
          }
          break;

        case "interview_locality_id":
          if (!isValueNull(field.value)) {
            fieldsToAdd = {
              interview_locality_id: field.value.id
            };
          }
          break;

        case "interview_type":
          if (!isValueNull(field.value)) {
            fieldsToAdd = {
              interview_type_label: handleOptionTypeFields(field.value)
            };
          }
          break;

        case "is_address_same_as_company":
          if (!isValueNull(field.value)) {
            fieldsToAdd = {
              is_address_same_as_company: field.value.id === "same"
            };
          }
          break;

        case "interview_address":
          if (!isValueNull(field.value)) {
            if (is_consultant) {
              fieldsToAdd = {
                client_address: field.value || "",
                interview_address: field.value || ""
              };
            } else {
              fieldsToAdd = {
                interview_address: field.value || ""
              };
            }
          }
          break;

        case "category_id":
          if (!isValueNull(field.value)) {
            fieldsToAdd = {
              category_id: field.value.id
            };
          }
          break;

        case "experience":
          let tempPayload = {};
          if (!isValueNull(field.value.min) && !isValueNull(field.value.max)) {
            tempPayload = {
              ...tempPayload,
              minExp: field.value.min < 0 ? 0 : field.value.min,
              maxExp: field.value.max < 0 ? 0 : field.value.max
            };
          }
          tempPayload = {
            ...tempPayload,
            is_fresher_allowed: field.value.isFresher
          };
          fieldsToAdd = tempPayload;
          break;

        case "salary":
          if (
            !isValueNull(field.value.min_salary) &&
            !isValueNull(field.value.max_salary)
          ) {
            fieldsToAdd = {
              minSal: field.value.min_salary,
              maxSal: field.value.max_salary,
              incentives: field.value.incentives || undefined,
              is_incentive: field.value.incentives > 0
            };
          }
          break;

        case "is_target_based":
          fieldsToAdd = {
            is_target_based: field.value
          };
          break;

        case "is_per_packet":
          fieldsToAdd = {
            is_per_packet: field.value
          };
          break;

        case "work_location":
          if (!isValueNull(field.value)) {
            fieldsToAdd = {
              job_type_location: field.value.id,
              is_wfh: field.value.id === "WORK_FROM_HOME"
            };
          }
          break;

        case "distance_preference":
          if (!isValueNull(field.value)) {
            fieldsToAdd = {
              distance_preferences: handleOptionTypeFields(field.value)
            };
          }
          break;

        case "job_description":
          if (!isValueNull(field.value)) {
            fieldsToAdd = {
              enhanced_job_description: field.value
            };
          }
          break;

        case "jobTimings":
          if (
            !isValueNull(field.value.startTime) &&
            !isValueNull(field.value.endTime)
          ) {
            fieldsToAdd = {
              job_end_time: field.value.endTime,
              job_start_time: field.value.startTime
            };
          }
          break;

        case "gender":
          if (!isValueNull(field.value)) {
            fieldsToAdd = {
              gender_req: field.value.id
            };
          }
          break;

        case "qualification":
          if (!isValueNull(field.value)) {
            fieldsToAdd = {
              minimum_qualification: field.value.id
            };
          }
          break;

        case "depositRequired":
          if (!isValueNull(field.value)) {
            const ans = field.value.id === "yes";
            fieldsToAdd = {
              is_additional_fee_applicable: ans
            };
          }
          break;

        case "depositAmount":
          if (field.value !== null && field.value !== undefined) {
            fieldsToAdd = {
              amount: field.value
            };
          }
          break;

        case "depositReason":
          if (!isValueNull(field.value)) {
            fieldsToAdd = {
              deposit_reason_id: handleOptionTypeFields(field.value)
            };
          }
          break;

        case "depositTime":
          fieldsToAdd = {
            deposit_time_id: handleOptionTypeFields(field.value)
          };
          break;

        case "shiftType":
          if (!isValueNull(field.value)) {
            fieldsToAdd = {
              shift_type: handleOptionTypeFields(field.value)
            };
          }
          break;

        case "asset":
          fieldsToAdd = {
            assets_v2: handleOptionTypeFields(field.value)
          };
          break;

        case "englishFluency":
          fieldsToAdd = {
            english_fluency: { option_id: field.value?.id || null },
            is_english_required: !!field.value?.id
          };
          break;

        case "regionalLanguages":
          fieldsToAdd = {
            regional_languages: handleOptionTypeFields(field.value)
          };
          break;

        case "job_type":
          if (!isValueNull(field.value)) {
            fieldsToAdd = {
              job_type: field.value.id
            };
          }
          break;

        case "is_contractual":
          fieldsToAdd = { is_contract: field.value };
          break;

        case "famous_location":
          fieldsToAdd = {
            famous_location_category_id: field.value.famous_category_id,
            famous_location_id: field.value.famous_location_id
          };
          break;

        case "workingDays":
          if (!isValueNull(field.value)) {
            fieldsToAdd = {
              working_days: handleOptionTypeFields(field.value)
            };
          }
          break;

        case "skills":
          fieldsToAdd = {
            job_skills: handleOptionTypeFields(field.value)
          };
          break;

        case "industry":
          if (!isValueNull(field.value)) {
            fieldsToAdd = {
              prior_experience_type: handleOptionTypeFields(field.value)
            };
          }
          break;
        case "industryExp":
          fieldsToAdd = {
            is_industry_exp: field.value
          };
          break;

        case "jobBenefits":
          fieldsToAdd = {
            job_perks: handleOptionTypeFields(field.value)
          };
          break;

        case "communication_preference":
          fieldsToAdd = {
            call_allowed: field.value?.checked,
            job_contact: field.value?.checked
              ? field.value?.currentPhone
              : undefined
          };
          break;

        default:
          if (!isValueNull(field.value)) {
            fieldsToAdd = {
              [field.id]: formatFieldValue(field)
            };
          }
          break;
      }

      return {
        ...acc,
        ...fieldsToAdd
      };
    }, {} as Record<string, any>);
};

export const getDraftPayload = () => {
  const {
    localState,
    jobPlan: jobPlanFromStore,
    jobId: jobIdFromStore,
    draftId: draftIdFromStore
  } = useTemporaryJobPostingStore.getState();
  // const {jobId: jobIdFromStore,draftId: draftIdFromStore} = useJobPostingStore.getState();
  const allFields = getAllAvailableFields(localState);
  if (!allFields.is_additional_fee_applicable) {
    delete allFields.amount;
    delete allFields.deposit_reason_id;
    delete allFields.deposit_time_id;
  }
  if (!allFields.is_wfh) {
    allFields.is_wfh = false;
  }

  // Check if it's a repost case by checking URL parameters
  const isJobRepost = isJobRepostFromUrl();
  const isPaymentPendingDraft = isDraftWithJobIdFromUrl();
  const isJobEdit = isJobEditFromUrl();

  // For reposts, don't include job_id in the payload
  const basePayload = { ...allFields, draft_id: draftIdFromStore };

  if (!isJobRepost) {
    basePayload.job_id = jobIdFromStore;
  }
  if (isPaymentPendingDraft || isJobEdit) {
    basePayload.is_draft = undefined;
  }

  return basePayload;
};

export const maxVacancy = 99;
export const MIN_SALARY = 999;
export const MAX_SALARY = 99999;
export const MIN_INCENTIVE = 100;
